import React, { useEffect, useState } from "react";
import Header from "../../reusables/Header";
import { useQuery } from "@apollo/client";
import { GET_CUSTOMER_POINT_DETAILS } from "../../constants";
import { Button, message, Spin, Typography } from "antd";
import { Link, useHistory } from "react-router-dom";
import { LogoBlack } from "../../assets/icons";
import Axios from "axios";
import { QRCode } from "react-qrcode-logo";
import SigninModal from "../homepage/partials/SigninModal";
import {
  CopyOutlined,
  ShoppingOutlined,
  ShopOutlined,
  GiftOutlined,
  ArrowRightOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from "@ant-design/icons";
import "./customer.scss";

const { Title, Text, Paragraph } = Typography;

const CustomerPage = () => {
  const history = useHistory();
  const { search } = history.location;

  const getStoredUser = () => {
    try {
      const stored = localStorage.getItem("user_customer_details");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  };

  const [currentUser, setCurrentUser] = useState(getStoredUser);
  const [newUser, setNewUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [signInModalVisible, setSignInModalVisible] = useState(false);

  // Parse phone from search query: handles both ?2348024212152 and ?phone=2348024212152
  const parseQueryPhone = (searchStr) => {
    if (!searchStr) return "";
    const raw = searchStr.replace(/^\?/, "");
    if (raw.startsWith("phone=")) {
      return decodeURIComponent(raw.replace(/^phone=/, ""));
    }
    return decodeURIComponent(raw);
  };

  const queryPhone = parseQueryPhone(search);
  const phone_number = queryPhone || currentUser?.phone_number || "";

  useEffect(() => {
    document.title = "Loystar — Member Loyalty Pass";
    const favicon = document.getElementById("favicon");
    if (favicon) {
      favicon.href = LogoBlack;
    }
  }, []);

  useEffect(() => {
    if (phone_number) {
      getCustomerByPhone(phone_number);
    }
  }, [phone_number]);

  const getCustomerByPhone = (phone_num) => {
    if (!phone_num) return;
    setLoading(true);
    setFetchError(null);

    Axios.get(
      `${process.env.REACT_APP_BASE_URL}/api/v2/get_urewards_customer?phone=${phone_num}`
    )
      .then((res) => {
        if (res.status === 200 && res.data) {
          setNewUser(res.data);
          // If customer details were retrieved, keep local state in sync
          try {
            localStorage.setItem("user_customer_details", JSON.stringify(res.data));
          } catch (e) {}
        }
      })
      .catch((err) => {
        const errorText =
          err.response?.data?.error ||
          err.response?.data?.message ||
          "Could not find loyalty account for this phone number.";
        setFetchError(errorText);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const { data: getCustomerPointDetails } = useQuery(
    GET_CUSTOMER_POINT_DETAILS,
    {
      variables: { customer_id: Number(newUser?.user_id) },
      skip: !newUser?.user_id,
      onError: (error) => {
        console.warn("GraphQL point details error:", error.message);
      },
    }
  );

  const graphQLPoints =
    getCustomerPointDetails?.getCustomerPointDetails
      ?.map((point) => Number(point.points_value))
      ?.reduce((a, b) => a + b, 0) || 0;

  const simplePointsSum = (newUser?.loyalty_points?.simple_points || []).reduce(
    (acc, p) => acc + (Number(p.accumulated_points) || 0),
    0
  );

  const stampsSum = (newUser?.loyalty_points?.stamps_program || []).reduce(
    (acc, p) => acc + (Number(p.accumulated_stamps) || 0),
    0
  );

  const totalPoints = graphQLPoints > 0 ? graphQLPoints : simplePointsSum;

  const customer_loyalty_programs = newUser?.loyalty_points
    ? [
        ...(newUser?.loyalty_points.simple_points || []),
        ...(newUser?.loyalty_points.stamps_program || []),
      ].filter(
        (p) =>
          p &&
          (p.active === undefined ||
            p.active === true ||
            p.active === 1 ||
            p.active === "1" ||
            p.status === true ||
            p.status === "1" ||
            String(p.status).toLowerCase() === "active") &&
          !p.deleted
      )
    : [];

  const copyLoyaltyCode = (code) => {
    if (navigator.clipboard && code) {
      navigator.clipboard.writeText(code);
      message.success("Member code copied to clipboard!");
    } else if (code) {
      message.info(`Member Code: ${code}`);
    }
  };

  const displayName =
    newUser?.first_name ||
    newUser?.last_name ||
    currentUser?.first_name ||
    currentUser?.last_name
      ? `${newUser?.first_name || currentUser?.first_name || ""} ${newUser?.last_name || currentUser?.last_name || ""}`.trim()
      : "Loyalty Member";

  const initials =
    displayName !== "Loyalty Member"
      ? displayName
          .split(" ")
          .map((n) => n[0])
          .slice(0, 2)
          .join("")
          .toUpperCase()
      : "LM";

  const memberCode = newUser?.loyalty_id || newUser?.phone_number || phone_number;
  const qrTargetUrl = `${process.env.REACT_APP_LOYSTAR_BASE_URL || "https://loystar.co"}/${newUser?.phone_number || phone_number}`;

  return (
    <>
      <Header
        titleComponent={
          <span style={{ fontWeight: 600, fontSize: 16 }}>
            {displayName}
          </span>
        }
        openLoginModal={() => setSignInModalVisible(true)}
      />

      <div className="customer-passbook-page">
        {loading ? (
          <div style={{ textAlign: "center", padding: "80px 20px" }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, color: "#6B6B76", fontSize: 14 }}>
              Loading your loyalty pass...
            </p>
          </div>
        ) : !phone_number && !currentUser && !newUser ? (
          /* NO PHONE & NOT SIGNED IN */
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #E8E8EE",
              borderRadius: 16,
              padding: "40px 24px",
              textAlign: "center",
              marginTop: 40,
            }}
          >
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 999,
                background: "#F6F6F8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                fontSize: 22,
                color: "#0F0F10",
              }}
            >
              <UserOutlined />
            </div>
            <Title level={3} style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
              My Loyalty Pass
            </Title>
            <Paragraph style={{ color: "#6B6B76", fontSize: 14, marginBottom: 24, lineHeight: 1.5 }}>
              Enter your mobile number to view your in-store member QR code, track points, and redeem rewards.
            </Paragraph>
            <Button
              type="primary"
              className="shopify-primary-btn"
              style={{ width: "100%" }}
              onClick={() => setSignInModalVisible(true)}
            >
              Sign In with Phone Number
            </Button>
            <div style={{ marginTop: 20 }}>
              <Link
                to="/transactions"
                style={{ color: "#6B6B76", fontSize: 13, textDecoration: "none" }}
              >
                Looking for order history? View orders →
              </Link>
            </div>
          </div>
        ) : fetchError && !newUser ? (
          /* PHONE ENTERED BUT NOT FOUND */
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #E8E8EE",
              borderRadius: 16,
              padding: "36px 20px",
              textAlign: "center",
              marginTop: 40,
            }}
          >
            <Title level={4} style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
              Account Not Found
            </Title>
            <Paragraph style={{ color: "#6B6B76", fontSize: 14, marginBottom: 20 }}>
              We could not find an existing loyalty membership for <strong>{phone_number}</strong>.
            </Paragraph>
            <Button
              type="primary"
              className="shopify-primary-btn"
              style={{ width: "100%" }}
              onClick={() => setSignInModalVisible(true)}
            >
              Try Another Number
            </Button>
            <Button
              type="default"
              className="shopify-secondary-btn"
              style={{ width: "100%", marginTop: 10 }}
              onClick={() => history.push("/home")}
            >
              Back to Loystar Shop
            </Button>
          </div>
        ) : (
          /* ACTIVE MEMBER PASSBOOK */
          <>
            {/* Member Profile Header */}
            <div className="member-profile-header">
              <div className="member-avatar">{initials}</div>
              <div className="member-info-col">
                <Title level={4} className="member-name">
                  {displayName}
                </Title>
                <div className="member-phone">
                  <span>{newUser?.phone_number || phone_number}</span>
                  <span className="member-badge-active">
                    <SafetyCertificateOutlined style={{ fontSize: 10 }} />
                    Active Member
                  </span>
                </div>
              </div>
            </div>

            {/* Digital Member Card (Shopify / Apple Wallet Style) */}
            <div className="member-pass-card">
              <div className="member-pass-top">
                <span className="pass-brand">LOYSTAR REWARDS</span>
                <span className="pass-chip">Digital Pass</span>
              </div>

              <div className="member-pass-middle">
                <div className="pass-points-label">Available Points</div>
                <div className="pass-points-value">
                  {Math.floor(Number(totalPoints))} <span>pts</span>
                </div>
                {stampsSum > 0 && (
                  <div className="pass-stamps-pill">
                    {stampsSum} {stampsSum === 1 ? "Stamp" : "Stamps"} Collected
                  </div>
                )}
              </div>

              <div className="member-pass-bottom">
                <span className="pass-code-text">
                  Member ID: {memberCode || "—"}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: "#A1A1AA",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                  onClick={() => copyLoyaltyCode(memberCode)}
                >
                  Copy
                </span>
              </div>
            </div>

            {/* In-Store QR Code Scan Card */}
            <div className="member-qr-card">
              <Title level={4} className="qr-title">
                In-Store Checkout Scan
              </Title>
              <Paragraph className="qr-desc">
                Present this QR code to the cashier at checkout to earn points or redeem rewards.
              </Paragraph>

              <div className="qr-canvas-box">
                <QRCode
                  size={160}
                  quietZone={8}
                  value={qrTargetUrl}
                  bgColor="#FFFFFF"
                  fgColor="#0A0A0A"
                  qrStyle="squares"
                  ecLevel="M"
                />
              </div>

              <div>
                <span
                  className="qr-code-pill"
                  onClick={() => copyLoyaltyCode(memberCode)}
                >
                  <span>Code: {memberCode}</span>
                  <CopyOutlined style={{ fontSize: 12, color: "#6B6B76" }} />
                </span>
              </div>
            </div>

            {/* Loyalty Programs & Rewards Progress */}
            {customer_loyalty_programs.length > 0 ? (
              <div style={{ marginBottom: 20 }}>
                <Title level={4} className="programs-section-title">
                  Your Reward Programs
                </Title>
                {customer_loyalty_programs.map((program, idx) => {
                  const currentScore = Number(
                    program.accumulated_points || program.accumulated_stamps || 0
                  );
                  const targetThreshold = Number(program.threshold || 100);
                  const progressPct = Math.min(
                    100,
                    Math.round((currentScore / (targetThreshold || 1)) * 100)
                  );
                  const isStamp = program.program_type === "StampsProgram";

                  return (
                    <div
                      key={program.id || program.program_name || idx}
                      className="program-card-minimal"
                    >
                      <div className="program-card-header">
                        <span className="program-card-name">
                          {program.program_name}
                        </span>
                        <span className="program-card-score">
                          {currentScore} {targetThreshold ? `/ ${targetThreshold}` : ""} {isStamp ? "stamps" : "pts"}
                        </span>
                      </div>

                      <div className="program-progress-bar-bg">
                        <div
                          className="program-progress-bar-fill"
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>

                      <div className="program-reward-row">
                        <span>
                          {program.reward ? (
                            <>
                              <GiftOutlined style={{ marginRight: 4 }} />
                              Reward: {program.reward}
                            </>
                          ) : (
                            "Earning rewards on purchases"
                          )}
                        </span>
                        {targetThreshold > currentScore && (
                          <span>
                            {targetThreshold - currentScore} more to unlock
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* EMPTY PROGRAMS HELPER */
              <div
                style={{
                  background: "#FFFFFF",
                  border: "1px solid #E8E8EE",
                  borderRadius: 14,
                  padding: "20px 18px",
                  marginBottom: 20,
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 24, marginBottom: 8 }}>🎁</div>
                <Title level={5} style={{ fontSize: 14, fontWeight: 600, margin: "0 0 4px" }}>
                  Earn Points With Every Visit
                </Title>
                <Paragraph style={{ color: "#6B6B76", fontSize: 13, margin: "0 0 14px", lineHeight: 1.4 }}>
                  Scan the QR code on printed receipts or show your member code above when shopping at participating Loystar merchants.
                </Paragraph>
                <Link to="/home" className="shopify-secondary-btn" style={{ width: "100%" }}>
                  Explore Loystar Shops
                </Link>
              </div>
            )}

            {/* Quick Actions */}
            <div style={{ marginTop: 24 }}>
              <Link to="/transactions" className="action-link-card">
                <div className="action-link-left">
                  <div className="action-link-icon">
                    <ShoppingOutlined />
                  </div>
                  <div>
                    <div className="action-link-title">Order History & Tracking</div>
                    <div className="action-link-desc">
                      View past receipts, deliveries, and order status
                    </div>
                  </div>
                </div>
                <ArrowRightOutlined style={{ color: "#9A9AA5", fontSize: 13 }} />
              </Link>

              <Link to="/home" className="action-link-card">
                <div className="action-link-left">
                  <div className="action-link-icon">
                    <ShopOutlined />
                  </div>
                  <div>
                    <div className="action-link-title">Discover Stores</div>
                    <div className="action-link-desc">
                      Browse stores and earn points across Loystar
                    </div>
                  </div>
                </div>
                <ArrowRightOutlined style={{ color: "#9A9AA5", fontSize: 13 }} />
              </Link>
            </div>
          </>
        )}
      </div>

      {signInModalVisible && (
        <SigninModal
          visible={signInModalVisible}
          onCancel={() => setSignInModalVisible(false)}
          setNewUser={(user) => {
            setCurrentUser(user);
            setNewUser(user);
            setSignInModalVisible(false);
            if (user?.phone_number) {
              getCustomerByPhone(user.phone_number);
            }
          }}
        />
      )}
    </>
  );
};

export default CustomerPage;
